import dayjs from '@jpi-cloud-web-pro/jpi-cloud-shared/utils/dayjs';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation, useNavigate } from 'react-router';

import { Button } from 'jpi-cloud-web-ui-kit';

import Link from '@/components/inputs/Link';

import appStoreBadge from '@/assets/img/app-store-badge.png';
import googlePlayBadge from '@/assets/img/google-play-badge.png';

import ResponsiveImage from '@/components/ResponsiveImage';
import { RecurringSubscriptionPayment } from '@/pages/Dashboard/types';
// @ts-ignore-next-line
import './footer.scss';
import locale from './locale';

const guestLinks = lang => ({
  common: [
    {
      href: '/legal/faq',
      ...locale['footer.faq'],
      isVisible: true,
    },
  ],
  info: [
    {
      href: '/legal/about',
      ...locale['aboutView.pro.title'],
      isVisible: true,
    },
    {
      href: '/legal/works-with',
      ...locale['slide.title.works-with'],
      isVisible: true,
    },
    {
      href: `/legal/privacy-policy/${lang}`,
      ...locale['footer.privacyPolicy'],
      isVisible: true,
    },
    {
      href: `/legal/terms-of-service/${lang}`,
      ...locale['footer.termsOfService'],
      isVisible: true,
    },
  ],
});

const userLinks = isAdmin => ({
  common: [
    {
      href: '/faq',
      ...locale['footer.faq'],
      isVisible: true,
    },
    {
      href: '/contact',
      ...locale['footer.contact'],
      isVisible: true,
    },
    {
      href: '/cookies',
      ...locale['footer.cookies'],
      isVisible: true,
    },
  ],
  info: [
    {
      href: '/about',
      ...locale['aboutView.pro.title'],
      isVisible: true,
    },
    {
      href: '/works-with',
      ...locale['slide.title.works-with'],
      isVisible: true,
    },
    {
      href: '/privacy-policy',
      ...locale['footer.privacyPolicy'],
      isVisible: true,
    },
    {
      href: '/terms-of-service',
      ...locale['footer.termsOfService'],
      isVisible: isAdmin,
    },
  ],
});

const NON_PREMIUM_BUTTON_PAGES = [
  '/store/subscriptions',
  '/store/shopping-cart',
  // '/store/payment-settings',
  // '/store/order-history',
  '/redeem-voucher',
  '/dashboard',
  '/checkout',
];

type FooterProps = {
  isPremiumButtonVisible: boolean;
  isAdmin: boolean;
  subscription?: RecurringSubscriptionPayment | null;
  isWelcome?: boolean;
  languageSelected?: string;
};

export const Footer = ({
  isPremiumButtonVisible = false,
  isAdmin = false,
  subscription = null,
  isWelcome = false,
  languageSelected = '',
}: FooterProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const menuLinks = useMemo(
    () => (isWelcome ? guestLinks(languageSelected) : userLinks(isAdmin)),
    [isWelcome, languageSelected, isAdmin],
  );

  const pathname = location.pathname || '';

  const nonPremiumBtnPages = NON_PREMIUM_BUTTON_PAGES.some(path => pathname.startsWith(path));

  const trialVisible = !subscription?.subscription;

  const showPremiumButton = Boolean(isPremiumButtonVisible) && !nonPremiumBtnPages;

  return (
    <footer className="footer-container">
      {showPremiumButton && (
        <div className="footer-premium row">
          <Button size="large" color="primary" onClick={() => navigate('/store/subscriptions')}>
            {trialVisible ? (
              <FormattedMessage id="button.activateFreeTrial" defaultMessage="Activate free trial" />
            ) : (
              <FormattedMessage id="button.getPremium" defaultMessage="Get premium" />
            )}
          </Button>
        </div>
      )}

      <div className="row">
        <div className="footer-links">
          <div className="footer-links-item">
            <div className="footer-link-text">
              <FormattedMessage id="footer.quick-links.short" defaultMessage="About application" />
            </div>
            {menuLinks.info.map(link =>
              link.isVisible ? (
                <div key={link.id} className="footer-link">
                  <Link to={link.href}>
                    <FormattedMessage id={link.id} defaultMessage={link.defaultMessage} />
                  </Link>
                </div>
              ) : null,
            )}
          </div>

          <div className="footer-links-item">
            <div className="footer-link-text">
              <FormattedMessage id="footer.support" defaultMessage="Support" />
            </div>
            {menuLinks.common.map(link =>
              link.isVisible ? (
                <div key={link.id} className="footer-link">
                  <Link to={link.href}>
                    <FormattedMessage id={link.id} defaultMessage={link.defaultMessage} />
                  </Link>
                </div>
              ) : null,
            )}
          </div>
        </div>
        <div className="footer-block">
          <div className="footer-text">
            <div className="download-heading">
              <FormattedMessage id="footer.download_pro_app.heading" defaultMessage="Download myUplink PRO app" />
            </div>
            <FormattedMessage
              id="footer.download-app"
              defaultMessage="Download the app and keep track of your system wherever you are."
            />
          </div>
          <div className="footer-badges">
            <a target="_blank" rel="noopener noreferrer" href="https://itunes.apple.com/app/id1466746198">
              <ResponsiveImage
                src={appStoreBadge}
                alt="App store link"
                imgProps={{
                  className: 'app-store-badge',
                }}
              />
            </a>
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://play.google.com/store/apps/details?id=com.myuplink.pro"
            >
              <ResponsiveImage
                src={googlePlayBadge}
                alt="Get it on Google Play"
                imgProps={{
                  className: 'google-play-badge',
                }}
              />
            </a>
          </div>
        </div>
      </div>

      <div className="footer-copyright row">
        <span>Copyright © {dayjs().year()} myUpTech AB</span>
      </div>
    </footer>
  );
};

Footer.propTypes = {
  subscription: PropTypes.object,
  languageSelected: PropTypes.string,
  isPremiumButtonVisible: PropTypes.bool,
  isWelcome: PropTypes.bool,
  isAdmin: PropTypes.bool,
};

export default Footer;
